import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Check, Sparkles, Crown, Zap, Shield, Loader2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_ICONS = {
  free: Zap,
  basic: Sparkles,
  premium: Crown,
  lifetime: Shield,
};

const TIER_COLORS = {
  free: { border: 'border-slate-700', accent: 'text-slate-400', bg: 'bg-slate-800/30' },
  basic: { border: 'border-cyan/30', accent: 'text-cyan', bg: 'bg-cyan/5' },
  premium: { border: 'border-gold/30', accent: 'text-gold', bg: 'bg-gold/5', badge: true },
  lifetime: { border: 'border-purple-500/30', accent: 'text-purple-400', bg: 'bg-purple-500/5' },
};

export default function PricingPage() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tiersRes, subRes, provRes] = await Promise.all([
          axios.get(`${API}/subscription/tiers`),
          token ? axios.get(`${API}/subscription/me`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: null }),
          axios.get(`${API}/subscription/providers`),
        ]);
        setTiers(tiersRes.data);
        setCurrentSub(subRes.data);
        const availableProviders = provRes.data.filter(p => p.available);
        setProviders(provRes.data);
        if (availableProviders.length > 0) setSelectedProvider(availableProviders[0].id);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleCheckout = async (tierId) => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!selectedProvider) {
      toast.error('No payment provider available. Contact admin.');
      return;
    }
    setCheckoutLoading(tierId);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        tier: tierId,
        payment_provider: selectedProvider,
        billing_cycle: billing,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.info(res.data.message || 'Checkout created');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-cyan animate-pulse">Loading plans...</div></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="pricing-page">
      <div className="text-center mb-10">
        <h1 className="font-heading text-4xl sm:text-5xl text-white mb-3">{t('pricing_title')}</h1>
        <p className="text-slate-400 text-lg max-w-xl mx-auto">{t('pricing_subtitle')}</p>

        {/* Billing toggle */}
        <div className="inline-flex items-center gap-2 mt-6 p-1 bg-navy-900/60 rounded-full border border-white/10">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-cyan text-navy-950' : 'text-slate-400 hover:text-white'}`}
            data-testid="billing-monthly"
          >Monthly</button>
          <button
            onClick={() => setBilling('yearly')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billing === 'yearly' ? 'bg-cyan text-navy-950' : 'text-slate-400 hover:text-white'}`}
            data-testid="billing-yearly"
          >Yearly <span className="text-xs opacity-75">Save 20%</span></button>
        </div>

        {/* Payment Provider Selector */}
        {providers.filter(p => p.available).length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <CreditCard size={14} className="text-slate-500" />
            <span className="text-slate-500 text-sm">Pay with:</span>
            {providers.filter(p => p.available).map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedProvider(p.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${selectedProvider === p.id ? 'bg-cyan/10 text-cyan border-cyan/30' : 'text-slate-500 border-white/10 hover:text-white'}`}
                data-testid={`provider-${p.id}`}
              >{p.name}</button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((tier, i) => {
          const colors = TIER_COLORS[tier.id] || TIER_COLORS.free;
          const Icon = TIER_ICONS[tier.id] || Zap;
          const isCurrentTier = currentSub?.tier === tier.id;
          const price = billing === 'yearly' && tier.price_yearly ? tier.price_yearly : tier.price;
          const isLifetime = tier.id === 'lifetime';

          return (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className={`relative h-full ${colors.bg} ${colors.border} border ${colors.badge ? 'ring-1 ring-gold/20' : ''}`} data-testid={`tier-card-${tier.id}`}>
                {colors.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy-950 text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className={colors.accent} size={22} />
                    <h3 className="text-white font-heading text-xl">{tier.name}</h3>
                  </div>

                  <div className="mb-5">
                    {tier.price === 0 ? (
                      <div className="text-3xl font-heading text-white">Free</div>
                    ) : isLifetime ? (
                      <div><span className="text-3xl font-heading text-white">${tier.price}</span><span className="text-slate-500 text-sm ml-1">one-time</span></div>
                    ) : (
                      <div>
                        <span className="text-3xl font-heading text-white">${price}</span>
                        <span className="text-slate-500 text-sm ml-1">/{billing === 'yearly' ? 'year' : 'month'}</span>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2.5 mb-6 flex-1">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <Check className={`${colors.accent} flex-shrink-0 mt-0.5`} size={14} />
                        <span className="text-slate-300">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentTier ? (
                    <Button disabled className="w-full rounded-xl h-10 bg-white/10 text-white" data-testid={`current-plan-${tier.id}`}>
                      Current Plan
                    </Button>
                  ) : tier.price === 0 ? (
                    <Button variant="outline" className="w-full rounded-xl h-10 border-white/10 text-slate-400" disabled>
                      Default
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleCheckout(tier.id)}
                      disabled={checkoutLoading === tier.id}
                      className={`w-full rounded-xl h-10 font-semibold ${
                        colors.badge ? 'bg-gold text-navy-950 hover:bg-gold/90' : 'bg-cyan text-navy-950 hover:bg-cyan/90'
                      }`}
                      data-testid={`checkout-${tier.id}`}
                    >
                      {checkoutLoading === tier.id ? <Loader2 className="animate-spin" size={16} /> : `Get ${tier.name}`}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {currentSub && currentSub.tier !== 'free' && (
        <div className="mt-8 text-center text-slate-500 text-sm">
          Current plan: <span className="text-white font-medium capitalize">{currentSub.tier}</span>
          {currentSub.expires_at && <span> &middot; Expires: {new Date(currentSub.expires_at).toLocaleDateString()}</span>}
          {currentSub.status === 'grace' && <span className="text-gold ml-2">(Grace period)</span>}
        </div>
      )}
    </div>
  );
}
